import apiClient from "../apiClient";

import type { UserInfo, UserToken } from "#/entity";
import {
	mapLoginResponse,
	mapMyInfoResponse,
	mapMySuperiorResponse,
	normalizeMyTeamResponse,
	toLoginPayload,
	toPayPasswordResetPayload,
	toPasswordResetPayload,
	toPasswordSmsPayload,
	toRefreshPayload,
	toRegisterPayload,
	toRegisterSmsPayload,
	toSetInfoPayload,
	type LoginResponse,
	type LoginPasswordEditReq,
	type MyInfo,
	type MyInfoResponse,
	type MySuperior,
	type MySuperiorResponse,
	type MyTeamData,
	type MyTeamParams,
	type MyTeamResponse,
	type PayPasswordResetReq,
	type PasswordResetReq,
	type RegisterReq,
	type SetInfoReq,
	type SignInReq,
} from "./userService.adapter";

export type {
	LoginPasswordEditForm,
	LoginPasswordEditReq,
	PayPasswordResetForm,
	PayPasswordResetReq,
	PasswordResetForm,
	PasswordResetReq,
	RegisterForm,
	RegisterReq,
	SignInReq,
} from "./userService.adapter";

export interface SignUpReq extends SignInReq {
	email: string;
}
export type SignInRes = UserToken & { user: UserInfo };

export enum UserApi {
	SignIn = "/v1/login",
	SignUp = "/auth/signup",
	Logout = "/v1/logout",
	Refresh = "/v1/refresh-token",
	MyInfo = "/v1/my-info",
	MySuperior = "/v1/my-superior",
	MyTeam = "/v1/my-team",
	MyRecommend = "/v1/my-user",
	SetInfo = "/v1/set-info",
	SendChangeLoginPasswordSms = "/v1/sms-changeloginpwd",
	SendChangePayPasswordSms = "/v1/sms-changepaypwd",
	RetrieveLoginPassword = "/v1/retrieve-loginpwd",
	RetrievePayPassword = "/v1/retrieve-paypwd",
	EditLoginPassword = "/v1/edit-loginpwd",
	SendRegisterSms = "/v1/sms-register",
	Register = "/v1/singup",
	User = "/user",
}

const signin = async (data: SignInReq): Promise<SignInRes> => {
	const res = await apiClient.post<LoginResponse>({ url: UserApi.SignIn, data: toLoginPayload(data) });
	return mapLoginResponse(res, data.username);
};
const getMyInfo = async (): Promise<MyInfo> => {
	const res = await apiClient.post<MyInfoResponse>({ url: UserApi.MyInfo });
	return mapMyInfoResponse(res);
};
const getMySuperior = async (): Promise<MySuperior> => {
	const res = await apiClient.post<MySuperiorResponse>({ url: UserApi.MySuperior });
	return mapMySuperiorResponse(res);
};
const getMyTeam = async (data: MyTeamParams): Promise<MyTeamData> =>
	normalizeMyTeamResponse(await apiClient.post<MyTeamResponse>({ url: UserApi.MyTeam, data }));
const getMyRecommend = async (data: MyTeamParams): Promise<MyTeamData> =>
	normalizeMyTeamResponse(await apiClient.post<MyTeamResponse>({ url: UserApi.MyRecommend, data }));
const setInfo = (data: SetInfoReq) => apiClient.post({ url: UserApi.SetInfo, data: toSetInfoPayload(data) });
const refresh = async (refreshToken: string): Promise<UserToken> => {
	const res = await apiClient.post<LoginResponse>({
		url: UserApi.Refresh,
		data: toRefreshPayload(refreshToken),
		skipAuthRefresh: true,
	});
	return {
		accessToken: res.access_token,
		refreshToken: res.refresh_token,
	};
};
const signup = (data: SignUpReq) => apiClient.post<SignInRes>({ url: UserApi.SignUp, data });
const logout = () => apiClient.post({ url: UserApi.Logout });
const findById = (id: string) => apiClient.get<UserInfo[]>({ url: `${UserApi.User}/${id}` });
const sendChangeLoginPasswordSms = (mobile: string) =>
	apiClient.post({ url: UserApi.SendChangeLoginPasswordSms, data: toPasswordSmsPayload(mobile) });
const sendChangePayPasswordSms = (mobile: string) =>
	apiClient.post({ url: UserApi.SendChangePayPasswordSms, data: toPasswordSmsPayload(mobile) });
const retrieveLoginPassword = (data: PasswordResetReq) =>
	apiClient.post({ url: UserApi.RetrieveLoginPassword, data: toPasswordResetPayload(data) });
const retrievePayPassword = (data: PayPasswordResetReq) =>
	apiClient.post({ url: UserApi.RetrievePayPassword, data: toPayPasswordResetPayload(data) });
const editLoginPassword = (data: LoginPasswordEditReq) => apiClient.post({ url: UserApi.EditLoginPassword, data });
const sendRegisterSms = (mobile: string) =>
	apiClient.post({ url: UserApi.SendRegisterSms, data: toRegisterSmsPayload(mobile) });
const register = (data: RegisterReq) => apiClient.post({ url: UserApi.Register, data: toRegisterPayload(data) });

export default {
	signin,
	getMyInfo,
	getMySuperior,
	getMyTeam,
	getMyRecommend,
	setInfo,
	refresh,
	signup,
	findById,
	logout,
	sendChangeLoginPasswordSms,
	sendChangePayPasswordSms,
	retrieveLoginPassword,
	retrievePayPassword,
	editLoginPassword,
	sendRegisterSms,
	register,
};
